import { getApiErrorCode, getApiErrorMessage } from '../../../utils/apiError'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'

import AddRoundedIcon from '@mui/icons-material/AddRounded'
import AutoStoriesRoundedIcon from '@mui/icons-material/AutoStoriesRounded'
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded'
import CheckRoundedIcon from '@mui/icons-material/CheckRounded'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import EditRoundedIcon from '@mui/icons-material/EditRounded'
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded'
import ReplayRoundedIcon from '@mui/icons-material/ReplayRounded'

import { getAttendanceProof } from '../../attendance-proof'
import { formatActivityRange, getDailyLogs, getFocusMetrics } from '../../daily-log'
import {
  getCompletionRateMetrics,
  getDailyPlan,
  getSessionalPlan,
  updateDailyCompletedQuantity,
} from '../../planner'
import {
  getAvailableReportTypes,
  getReportHeaders,
  getReportWritingStatuses,
  REPORT_REPORT_TYPE_NAME_MAP,
} from '../../reports'
import { getReportDetailPath, getWeeklyPlanPath, ROUTES } from '../../../constants/routes'
import { TIMER_STATUS, useActivityTimer } from '../../timer'
import { selectCurrentMember } from '../../profile'
import { getServiceDate, getServiceMoment } from '../../../utils/serviceDate'
import useModal from '../../../hooks/useModal'
import useServiceDate from '../../../hooks/useServiceDate'
import { Page, ServiceDayHelp, SkeletonList } from '../../../components/ui'
import * as H from './Home.styles'
import {
  formatFocusTime,
  getHomePlanCompleted,
  getHomePlanTotal,
  getHomePlanUnit,
  getPendingHomePlans,
  getRecentLogs,
  truncateHomeSubjectName,
} from '../lib/homeUtils'

function getErrorMessage(error, fallback) {
  return getApiErrorMessage(error, fallback)
}

function isMissingSessionalPlan(error) {
  const responseData = error?.response?.data
  const serverMessages = [
    typeof responseData === 'string' ? responseData : undefined,
    responseData?.serverMessage,
    responseData?.message,
    error?.message,
  ]

  return error?.response?.status === 404
    || getApiErrorCode(error) === 'SESSIONAL_PLAN_NOT_FOUND'
    || serverMessages.some((message) => (
      typeof message === 'string' && message.trim().toLowerCase() === 'sessional plan not found.'
    ))
}

const DAY_NAMES = ['일', '월', '화', '수', '목', '금', '토']

function SoftBadgeCheckIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false">
      <path
        d="M10.58 3.23Q12 1.3 13.42 3.23Q14.83 5.16 17.2 4.8Q19.57 4.43 19.2 6.8Q18.84 9.17 20.77 10.58Q22.7 12 20.77 13.42Q18.84 14.83 19.2 17.2Q19.57 19.57 17.2 19.2Q14.83 18.84 13.42 20.77Q12 22.7 10.58 20.77Q9.17 18.84 6.8 19.2Q4.43 19.57 4.8 17.2Q5.16 14.83 3.23 13.42Q1.3 12 3.23 10.58Q5.16 9.17 4.8 6.8Q4.43 4.43 6.8 4.8Q9.17 5.16 10.58 3.23Z"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path d="m8.3 12.2 2.5 2.5 5-5.2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}

function HomePage() {
  const currentMember = useSelector(selectCurrentMember)
  const timer = useActivityTimer()
  const { openModal } = useModal()
  const navigate = useNavigate()
  const today = useServiceDate()
  const planSkipStorageKey = `akive:home:plan-skipped:${today}`
  const [dailyPlans, setDailyPlans] = useState([])
  const [dailyLogs, setDailyLogs] = useState([])
  const [focusMetrics, setFocusMetrics] = useState()
  const [completionRate, setCompletionRate] = useState()
  const [attendanceInfo, setAttendanceInfo] = useState(undefined)
  const [availableReportTypes, setAvailableReportTypes] = useState([])
  const [reportWritingStatuses, setReportWritingStatuses] = useState({})
  const [weeklyPlanPath, setWeeklyPlanPath] = useState(ROUTES.PLANS)
  const [selectedPlanId, setSelectedPlanId] = useState(null)
  const [editingCompletionId, setEditingCompletionId] = useState(null)
  const [completionDraft, setCompletionDraft] = useState('')
  const [completionError, setCompletionError] = useState('')
  const [isSavingCompletion, setIsSavingCompletion] = useState(false)
  const [isOpeningWrittenReport, setIsOpeningWrittenReport] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [planError, setPlanError] = useState('')
  const [hasPartialError, setHasPartialError] = useState(false)
  const [hasSkippedPlan, setHasSkippedPlan] = useState(() => {
    try {
      return window.sessionStorage.getItem(planSkipStorageKey) === 'true'
    } catch {
      return false
    }
  })

  useEffect(() => {
    try {
      setHasSkippedPlan(window.sessionStorage.getItem(planSkipStorageKey) === 'true')
    } catch {
      setHasSkippedPlan(false)
    }
  }, [planSkipStorageKey])

  const loadPlanShortcut = useCallback(async (plans, serviceDateAtRequest) => {
    const firstPlan = plans.find((item) => Number(item.amount?.quantityAmount || 0) > 0)
    if (!firstPlan?.sessionalPlanId) return

    try {
      const result = await getSessionalPlan(firstPlan.sessionalPlanId)
      const totalQuantity = (result.data?.totalQuantities || []).find(
        (item) => item.totalQuantityId === firstPlan.totalQuantityId,
      )
      const weeklyQuantity = (totalQuantity?.weeklyQuantities || []).find(
        (item) => item.weeklyQuantityId === firstPlan.weeklyQuantityId,
      )
      const weekNum = weeklyQuantity?.sessionWeek?.weekNum
      if (weekNum && getServiceDate() === serviceDateAtRequest) {
        setWeeklyPlanPath(`${getWeeklyPlanPath(firstPlan.sessionalPlanId)}?weekNum=${weekNum}`)
      }
    } catch {
      if (getServiceDate() === serviceDateAtRequest) setWeeklyPlanPath(ROUTES.PLANS)
    }
  }, [])

  const loadHome = useCallback(async () => {
    setIsLoading(true)
    setPlanError('')
    setHasPartialError(false)
    setWeeklyPlanPath(ROUTES.PLANS)

    const results = await Promise.allSettled([
      getDailyPlan(today),
      getDailyLogs(today),
      getFocusMetrics({ startOn: today, endOn: today }),
      getCompletionRateMetrics({ startOn: today, endOn: today }),
      getAttendanceProof(today),
      getAvailableReportTypes(today),
      getReportWritingStatuses(),
    ])

    if (getServiceDate() !== today) return

    if (results[0].status === 'fulfilled') {
      const plans = results[0].value.data?.dailyPlans || []
      setDailyPlans(plans)
      loadPlanShortcut(plans, today)
    } else {
      setPlanError(getErrorMessage(results[0].reason, '오늘의 학습 계획을 불러오지 못했어요.'))
    }
    if (results[1].status === 'fulfilled') setDailyLogs(results[1].value.data?.dailyLogs || [])
    if (results[2].status === 'fulfilled') setFocusMetrics(results[2].value.data)
    if (results[3].status === 'fulfilled') setCompletionRate(results[3].value.data?.completionRate)
    if (results[4].status === 'fulfilled') {
      setAttendanceInfo(results[4].value.data)
    } else if (
      results[4].reason?.response?.status === 404
      && results[4].reason?.response?.data?.code === 'ATTENDANCE_PROOF_NOT_FOUND'
    ) {
      setAttendanceInfo(null)
    }
    if (results[5].status === 'fulfilled') {
      setAvailableReportTypes(results[5].value.data?.reportTypes || [])
    } else if (isMissingSessionalPlan(results[5].reason)) {
      setAvailableReportTypes([])
    }
    if (results[6].status === 'fulfilled') {
      setReportWritingStatuses(results[6].value.data || {})
    } else if (isMissingSessionalPlan(results[6].reason)) {
      setReportWritingStatuses({})
    }

    const partialFailures = results.slice(1).filter((result, index) => {
      if (result.status !== 'rejected') return false
      if (index === 3) {
        return !(result.reason?.response?.status === 404
          && result.reason?.response?.data?.code === 'ATTENDANCE_PROOF_NOT_FOUND')
      }
      if ((index === 4 || index === 5) && isMissingSessionalPlan(result.reason)) return false
      return true
    })
    setHasPartialError(partialFailures.length > 0)
    setIsLoading(false)
  }, [loadPlanShortcut, today])

  useEffect(() => {
    loadHome()
  }, [loadHome])

  const refreshDailyLogSummary = useCallback(async () => {
    const [logsResult, metricsResult] = await Promise.allSettled([
      getDailyLogs(today),
      getFocusMetrics({ startOn: today, endOn: today }),
    ])

    if (logsResult.status === 'fulfilled') setDailyLogs(logsResult.value.data?.dailyLogs || [])
    if (metricsResult.status === 'fulfilled') setFocusMetrics(metricsResult.value.data)
    if (logsResult.status === 'rejected' || metricsResult.status === 'rejected') setHasPartialError(true)
  }, [today])

  useEffect(() => {
    if (!timer.saveVersion || timer.lastSavedLogOn !== today) return
    refreshDailyLogSummary()
  }, [refreshDailyLogSummary, timer.lastSavedLogOn, timer.saveVersion, today])

  const plannedItems = useMemo(
    () => dailyPlans.filter((item) => getHomePlanTotal(item) > 0),
    [dailyPlans],
  )
  const pendingPlans = useMemo(() => getPendingHomePlans(plannedItems), [plannedItems])
  const learningLogs = useMemo(
    () => dailyLogs.filter((log) => log.dailyLogType !== 'ATTENDANCE'),
    [dailyLogs],
  )
  const recentLogs = useMemo(() => getRecentLogs(learningLogs), [learningLogs])
  const allPlanCount = plannedItems.length
  const allPlansComplete = allPlanCount > 0 && pendingPlans.length === 0
  const hasDailyRecord = learningLogs.length > 0
  const hasStudyRecord = learningLogs.some((log) => log.dailyLogType === 'STUDY')
  const timerIsActive = timer.status !== TIMER_STATUS.IDLE
  const planWasSkipped = allPlanCount === 0 && hasSkippedPlan
  const executionComplete = (allPlansComplete || (planWasSkipped && hasStudyRecord)) && !timerIsActive
  const { totalReportCount, writtenReportCount } = useMemo(() => {
    const currentReportStatusItems = Object.values(reportWritingStatuses)
      .flatMap((items) => (Array.isArray(items) ? items : items ? [items] : []))
      .filter((item) => item?.reportOn === today)

    return {
      totalReportCount: currentReportStatusItems.length,
      writtenReportCount: currentReportStatusItems.filter((item) => item?.isWritten).length,
    }
  }, [reportWritingStatuses, today])
  const allReportsComplete = allPlanCount > 0
    && totalReportCount > 0
    && writtenReportCount === totalReportCount
  const featuredPlan = pendingPlans.find((item) => item.dailyQuantityId === selectedPlanId) || pendingPlans[0]
  const startLearning = (item) => {
    if (timer.status !== TIMER_STATUS.IDLE) {
      openModal({
        type: 'alert',
        props: { contents: '이미 실행 중이거나 저장을 기다리는 활동이 있어요. 화면 아래의 스톱워치에서 이어서 진행해 주세요.' },
      })
      return
    }

    if (!item) {
      try {
        window.sessionStorage.setItem(planSkipStorageKey, 'true')
      } catch {
        // 저장소 접근이 제한돼도 현재 화면의 학습 시작 흐름은 유지한다.
      }
      setHasSkippedPlan(true)
    }

    timer.prepare({
      dailyLogType: 'STUDY',
      contents: item?.subject?.name || '',
      source: item ? {
        type: 'PLAN',
        sessionalPlanId: item.sessionalPlanId,
        totalQuantityId: item.totalQuantityId,
        weeklyQuantityId: item.weeklyQuantityId,
        dailyQuantityId: item.dailyQuantityId,
      } : null,
    })
  }

  const openCompletionEditor = (item) => {
    setSelectedPlanId(item.dailyQuantityId)
    setEditingCompletionId(item.dailyQuantityId)
    setCompletionDraft(String(getHomePlanCompleted(item)))
    setCompletionError('')
  }

  const closeCompletionEditor = () => {
    setEditingCompletionId(null)
    setCompletionDraft('')
    setCompletionError('')
  }

  const saveCompletion = async (event, item) => {
    event.preventDefault()
    const nextAmount = Number(completionDraft)
    const total = getHomePlanTotal(item)

    if (!Number.isFinite(nextAmount) || nextAmount < 0 || nextAmount > total) {
      setCompletionError(`완료량은 0부터 ${total}${getHomePlanUnit(item)} 사이로 입력해 주세요.`)
      return
    }

    setIsSavingCompletion(true)
    setCompletionError('')
    try {
      await updateDailyCompletedQuantity(
        item.sessionalPlanId,
        item.totalQuantityId,
        item.weeklyQuantityId,
        item.dailyQuantityId,
        { targetDailyCompletedAmount: nextAmount },
      )
      setDailyPlans((current) => current.map((plan) => (
        plan.dailyQuantityId === item.dailyQuantityId
          ? { ...plan, amount: { ...plan.amount, completedQuantityAmount: nextAmount } }
          : plan
      )))
      closeCompletionEditor()

      try {
        const result = await getCompletionRateMetrics({ startOn: today, endOn: today })
        setCompletionRate(result.data?.completionRate)
      } catch {
        setHasPartialError(true)
      }
    } catch (error) {
      setCompletionError(getErrorMessage(error, '완료량을 저장하지 못했어요. 다시 시도해 주세요.'))
    } finally {
      setIsSavingCompletion(false)
    }
  }

  const handleAttendanceChanged = async (nextAttendance, options = {}) => {
    if (nextAttendance === undefined) {
      await loadHome()
      return
    }
    setAttendanceInfo(nextAttendance)
    if (options.removeAttendanceLogs) {
      setDailyLogs((currentLogs) => currentLogs.filter((log) => log.dailyLogType !== 'ATTENDANCE'))
    }
    if (options.refreshDailyLogs === false) return
    try {
      const result = await getDailyLogs(today)
      setDailyLogs(result.data?.dailyLogs || [])
    } catch {
      setHasPartialError(true)
    }
  }

  const openAttendance = () => {
    openModal({
      type: 'attendanceProof',
      props: { proofOn: today, attendanceInfo, onChanged: handleAttendanceChanged },
    })
  }

  const openDailyLogEditor = () => {
    openModal({
      type: 'dailyLogForm',
      props: {
        isModify: false,
        dailyLogContentsList: dailyLogs,
        logOn: today,
        onSaved: refreshDailyLogSummary,
      },
    })
  }

  const openWrittenReport = async () => {
    setIsOpeningWrittenReport(true)
    try {
      const result = await getReportHeaders({ page: 0, size: 20 })
      const todayReports = (result.data?.reportHeaders || []).filter((report) => report?.reportOn === today)

      if (todayReports.length === 1) {
        const report = todayReports[0]
        navigate(`${getReportDetailPath(report.reportId)}?reportType=${report.reportType}&date=${report.reportOn}`)
        return
      }
      if (todayReports.length > 1) {
        openModal({ type: 'writtenReportSelection', props: { reports: todayReports } })
        return
      }
      navigate(ROUTES.REPORTS)
    } catch {
      navigate(ROUTES.REPORTS)
    } finally {
      setIsOpeningWrittenReport(false)
    }
  }

  const openReportEditor = () => {
    if (allPlanCount === 0) {
      navigate(ROUTES.PLANS)
      return
    }
    if (allReportsComplete) {
      openWrittenReport()
      return
    }
    if (availableReportTypes.length === 1) {
      navigate(`${ROUTES.REPORT_NEW}?reportType=${availableReportTypes[0].code}&date=${today}`)
      return
    }
    if (availableReportTypes.length > 1) {
      openModal({ type: 'reportTypeSelection', props: { reportTypes: availableReportTypes, date: today } })
      return
    }
    navigate(ROUTES.REPORTS)
  }

  const serviceMoment = getServiceMoment()
  const dateHeading = `${serviceMoment.format('M월 D일')} ${DAY_NAMES[serviceMoment.day()]}요일`
  const planStageActive = allPlanCount === 0 && !planWasSkipped
  const executionStageActive = !executionComplete && (allPlanCount > 0 || planWasSkipped || timerIsActive)
  const recordStageActive = !hasDailyRecord && executionComplete
  const reportStageActive = !allReportsComplete
    && executionComplete
    && hasDailyRecord
    && availableReportTypes.length > 0
  const reportTypeLabels = useMemo(() => availableReportTypes.map(({ code, label }) => (
    label || REPORT_REPORT_TYPE_NAME_MAP[code] || code
  )), [availableReportTypes])
  const planPreviews = useMemo(
    () => (pendingPlans.length > 0 ? pendingPlans : plannedItems).slice(0, 3),
    [pendingPlans, plannedItems],
  )
  const latestLog = recentLogs[0]
  const latestLogTime = latestLog ? formatActivityRange(latestLog).split('–')[0] : ''
  const reportDescription = isLoading
    ? '작성 가능한 회고를 확인하고 있어요.'
    : allPlanCount === 0
      ? '회고를 시작하려면 먼저 학습 계획이 필요해요.'
    : availableReportTypes.length === 0
      ? allReportsComplete
        ? '작성할 수 있는 회고를 모두 마쳤어요.'
        : '현재 작성할 수 있는 회고가 없어요.'
      : availableReportTypes.length === 1
        ? `${reportTypeLabels[0].replace(' 리포트', '')} 회고를 작성할 수 있어요.`
        : `작성 가능한 회고가 ${availableReportTypes.length}개 있어요.`
  const reportActionLabel = isOpeningWrittenReport
    ? '리포트 불러오는 중…'
    : isLoading
    ? '확인 중…'
    : allPlanCount === 0
      ? '계획 만들기'
      : allReportsComplete
        ? '작성한 리포트 확인'
        : availableReportTypes.length === 0
          ? ''
          : writtenReportCount > 0
            ? '남은 회고 작성하기'
            : availableReportTypes.length > 1
              ? '회고 기간 선택'
              : '회고 작성하기'

  return (
    <Page>
      <H.HomeHeader>
        <H.HeaderCopy>
          <H.TodayLabel>
            <span>TODAY</span>
            <ServiceDayHelp label="오늘 학습일 기준 안내" showCurrentServiceDay />
          </H.TodayLabel>
          <h1>{currentMember?.userDetail?.nickname || '학습자'}님의 오늘 학습 여정</h1>
          <p>{dateHeading} · 계획부터 회고까지, 오늘의 배움을 하나의 흐름으로 이어가요.</p>
        </H.HeaderCopy>
      </H.HomeHeader>

      {hasPartialError && (
        <H.PartialError role="status">
          <span>오늘의 일부 정보를 불러오지 못했어요.</span>
          <button type="button" onClick={loadHome}>
            <ReplayRoundedIcon aria-hidden="true" fontSize="inherit" />
            다시 불러오기
          </button>
        </H.PartialError>
      )}

      <H.Journey aria-label="오늘의 학습 여정">
        <H.JourneyStep>
          <H.StepRail>
            <H.StepMarker $complete={allPlanCount > 0} $active={planStageActive} aria-label={allPlanCount > 0 ? '계획 단계 완료' : planWasSkipped ? '계획 단계 건너뜀' : '계획 단계 진행 가능'}>
              {allPlanCount > 0 ? <CheckRoundedIcon aria-hidden="true" /> : planWasSkipped ? '–' : '1'}
            </H.StepMarker>
          </H.StepRail>
          <H.JourneyCard $active={planStageActive}>
            <H.CompactCardBody>
              <H.CompactCopy>
                <span>계획</span>
                <h2>오늘의 학습</h2>
                {isLoading ? (
                  <H.CompactLoading><SkeletonList rows={1} label="오늘의 학습 계획을 불러오는 중" /></H.CompactLoading>
                ) : planError ? (
                  <H.CompactMessage>{planError}</H.CompactMessage>
                ) : planPreviews.length > 0 ? (
                  <H.PlanChips>
                    {planPreviews.map((item) => {
                      const total = getHomePlanTotal(item)
                      const completed = getHomePlanCompleted(item)
                      const progress = total > 0 ? Math.round((completed / total) * 100) : 0
                      const isSelected = item.dailyQuantityId === featuredPlan?.dailyQuantityId
                      const isEditing = editingCompletionId === item.dailyQuantityId
                      const subjectName = item.subject?.name || '이름 없는 학습 항목'
                      const displaySubjectName = truncateHomeSubjectName(subjectName)
                      return (
                        <H.PlanChip key={item.dailyQuantityId} $selected={isSelected} $editing={isEditing}>
                          <H.PlanChipTop>
                            <button type="button" title={subjectName} aria-label={`${subjectName} 이어서 보기`} aria-pressed={isSelected} onClick={() => setSelectedPlanId(item.dailyQuantityId)}>
                              <H.PlanSelection $selected={isSelected} aria-hidden="true" />
                              <span>{displaySubjectName}</span><strong>{progress}%</strong>
                            </button>
                            <button type="button" aria-label={`완료량 수정, 현재 ${completed} / ${total}${getHomePlanUnit(item)}`} onClick={() => openCompletionEditor(item)}><EditRoundedIcon aria-hidden="true" /></button>
                          </H.PlanChipTop>
                          {isEditing && (
                            <H.CompactCompletionForm aria-label={`${item.subject?.name || '학습 항목'} 완료량 수정`} $error={Boolean(completionError)} onSubmit={(event) => saveCompletion(event, item)}>
                              <label htmlFor={`home-completion-${item.dailyQuantityId}`}>완료량</label>
                              <input id={`home-completion-${item.dailyQuantityId}`} aria-label="완료량" type="number" min="0" max={total} step="1" value={completionDraft} aria-invalid={Boolean(completionError)} onChange={(event) => { setCompletionDraft(event.target.value); setCompletionError('') }} autoFocus />
                              <H.CompletionLimit>/ {total}{getHomePlanUnit(item)}</H.CompletionLimit>
                              <H.InlineIconButton type="submit" aria-label="완료량 저장" disabled={isSavingCompletion}><CheckRoundedIcon aria-hidden="true" /></H.InlineIconButton>
                              <H.InlineIconButton type="button" $muted aria-label="완료량 수정 취소" disabled={isSavingCompletion} onClick={closeCompletionEditor}><CloseRoundedIcon aria-hidden="true" /></H.InlineIconButton>
                              {completionError && <p role="alert">{completionError}</p>}
                            </H.CompactCompletionForm>
                          )}
                        </H.PlanChip>
                      )
                    })}
                  </H.PlanChips>
                ) : <H.CompactMessage>{allPlansComplete ? '오늘 계획을 모두 완료했어요.' : '오늘 계획이 아직 없어요.'}</H.CompactMessage>}
              </H.CompactCopy>
              <H.CompactLink type="button" onClick={() => navigate(weeklyPlanPath)}>전체 계획<ChevronRightRoundedIcon aria-hidden="true" /></H.CompactLink>
            </H.CompactCardBody>
          </H.JourneyCard>
        </H.JourneyStep>

        <H.JourneyStep>
          <H.StepRail>
            <H.StepMarker $complete={executionComplete} $active={executionStageActive} aria-label={executionComplete ? '실행 단계 완료' : '실행 단계'}>
              {executionComplete ? <CheckRoundedIcon aria-hidden="true" /> : '2'}
            </H.StepMarker>
          </H.StepRail>
          <H.JourneyCard $active={executionStageActive}>
            <H.CompactCardBody>
              <H.CompactCopy>
                <span>실행</span>
                <h2>출석하고 집중해서 학습해요</h2>
                <H.MetaChips $tone="execution">
                  <H.AttendanceChip type="button" $complete={Boolean(attendanceInfo)} disabled={attendanceInfo === undefined} onClick={openAttendance}>
                    {attendanceInfo === undefined
                      ? '출석 확인 중'
                      : attendanceInfo
                        ? <><SoftBadgeCheckIcon />출석 인증 완료</>
                        : '출석 인증하기'}
                  </H.AttendanceChip>
                  <span>집중 {formatFocusTime(focusMetrics?.focusTimeSec)}</span>
                  {completionRate !== undefined && completionRate !== null && <span>달성률 {Math.round(completionRate)}%</span>}
                </H.MetaChips>
              </H.CompactCopy>
              <H.CompactPrimaryButton
                type="button"
                title={featuredPlan?.subject?.name || undefined}
                aria-label={featuredPlan ? `${featuredPlan.subject?.name || '선택한 항목'} 학습 시작하기` : undefined}
                onClick={() => startLearning(featuredPlan || null)}
              >
                <PlayArrowRoundedIcon aria-hidden="true" />
                {timerIsActive
                  ? '학습 이어가기'
                  : featuredPlan
                    ? `${truncateHomeSubjectName(featuredPlan.subject?.name || '선택한 항목')} 학습 시작하기`
                    : '계획 없이 시작'}
              </H.CompactPrimaryButton>
            </H.CompactCardBody>
          </H.JourneyCard>
        </H.JourneyStep>

        <H.JourneyStep>
          <H.StepRail>
            <H.StepMarker $complete={hasDailyRecord} $active={recordStageActive} aria-label={hasDailyRecord ? '기록 단계 완료' : recordStageActive ? '기록 단계 진행 가능' : '기록 단계'}>{hasDailyRecord ? <CheckRoundedIcon aria-hidden="true" /> : '3'}</H.StepMarker>
          </H.StepRail>
          <H.JourneyCard $active={recordStageActive}>
            <H.CompactCardBody>
              <H.CompactCopy>
                <span>기록</span>
                <h2>하루 기록</h2>
                <H.LogPreview>{latestLog ? `“${latestLog.contents || '내용 없는 기록'}”` : '아직 오늘 기록이 없어요.'}</H.LogPreview>
                <H.MetaChips $tone="record">
                  {hasDailyRecord && <H.CompleteChip><SoftBadgeCheckIcon />하루 기록 완료</H.CompleteChip>}
                  <span>작성한 기록 {learningLogs.length}개</span>
                  {latestLogTime && <span>마지막 기록 {latestLogTime}</span>}
                </H.MetaChips>
              </H.CompactCopy>
              <H.RecordActions $hasRecord={hasDailyRecord}>
                <H.RecordAddButton type="button" onClick={openDailyLogEditor}>
                  <AddRoundedIcon aria-hidden="true" />
                  {hasDailyRecord ? '기록 추가하기' : '하루 기록 남기기'}
                </H.RecordAddButton>
                {hasDailyRecord && (
                  <H.CompactLink type="button" aria-label="전체 기록" onClick={() => navigate(ROUTES.DAILY_LOGS, { state: { date: today } })}>
                    전체 기록<ChevronRightRoundedIcon aria-hidden="true" />
                  </H.CompactLink>
                )}
              </H.RecordActions>
            </H.CompactCardBody>
          </H.JourneyCard>
        </H.JourneyStep>

        <H.JourneyStep $last>
          <H.StepRail>
            <H.StepMarker $complete={allReportsComplete} $active={reportStageActive} aria-label={allReportsComplete ? '회고 및 리포트 단계 완료' : reportStageActive ? '회고 및 리포트 단계 진행 가능' : '회고 및 리포트 단계'}>{allReportsComplete ? <CheckRoundedIcon aria-hidden="true" /> : '4'}</H.StepMarker>
          </H.StepRail>
          <H.JourneyCard $active={reportStageActive}>
            <H.CompactCardBody>
              <H.CompactCopy>
                <span>회고</span>
                <h2>{reportDescription}</h2>
                {(writtenReportCount > 0 || reportTypeLabels.length > 0) && (
                  <H.MetaChips $tone="review">
                    {writtenReportCount > 0 && (
                      <H.CompleteChip>
                        <SoftBadgeCheckIcon />
                        {writtenReportCount > 1 ? `회고 ${writtenReportCount}개 작성 완료` : '회고 작성 완료'}
                      </H.CompleteChip>
                    )}
                    {reportTypeLabels.map((label) => <H.ReportTypeChip key={label}>{label.replace(' 리포트', '')}</H.ReportTypeChip>)}
                  </H.MetaChips>
                )}
              </H.CompactCopy>
              {reportActionLabel && (
                <H.CompactReviewButton type="button" disabled={isLoading || isOpeningWrittenReport} onClick={openReportEditor}>
                  {allPlanCount === 0 ? <AddRoundedIcon aria-hidden="true" /> : <AutoStoriesRoundedIcon aria-hidden="true" />}
                  {reportActionLabel}
                </H.CompactReviewButton>
              )}
            </H.CompactCardBody>
          </H.JourneyCard>
        </H.JourneyStep>
      </H.Journey>
    </Page>
  )
}

export default HomePage
